import { FilterRail } from '@/components/ui/FilterRail'

/**
 * Filter rail for Product↔Customer Pricing. Composes the shared
 * `<FilterRail>` shell (see `MarginsFilterRail` for the original
 * extraction) with this module's own content: a margin-floor % input,
 * and salesperson / vendor substring filters forwarded straight
 * through to the backend.
 *
 * Fully controlled — no state lives in this component. The page owns
 * every value (including the raw, pre-debounce text for the two text
 * inputs) so debounce timing stays visible in one place, matching the
 * existing product-search debounce already in
 * `ProductCustomerPricingPage.tsx`.
 */

const DEFAULT_MARGIN_FLOOR_PCT = 15

export interface PricingFilterRailProps {
  marginFloorPct: number
  onMarginFloorPctChange: (value: number) => void
  salesperson: string
  onSalespersonChange: (value: string) => void
  vendorName: string
  onVendorNameChange: (value: string) => void
  collapsed: boolean
  onToggleCollapsed: () => void
}

export function PricingFilterRail({
  marginFloorPct,
  onMarginFloorPctChange,
  salesperson,
  onSalespersonChange,
  vendorName,
  onVendorNameChange,
  collapsed,
  onToggleCollapsed,
}: PricingFilterRailProps) {
  const activeCount =
    (marginFloorPct !== DEFAULT_MARGIN_FLOOR_PCT ? 1 : 0) +
    (salesperson.trim() ? 1 : 0) +
    (vendorName.trim() ? 1 : 0)

  return (
    <FilterRail collapsed={collapsed} onToggleCollapsed={onToggleCollapsed} activeCount={activeCount}>
      <section className="flex flex-col gap-1.5">
        <label htmlFor="pricing-margin-floor" className="text-role-section-label">
          Margin floor
        </label>
        <div className="flex items-center gap-1.5">
          <input
            id="pricing-margin-floor"
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step={0.5}
            value={marginFloorPct}
            onChange={(e) => {
              const next = e.target.valueAsNumber
              if (!Number.isNaN(next)) onMarginFloorPctChange(next)
            }}
            className="h-7 w-16 rounded-xs border border-border-default bg-surface px-1.5 text-xs text-primary tabular-nums focus:border-border-focus focus:outline-none"
          />
          <span className="text-role-metadata text-tertiary">%</span>
        </div>
        <p className="text-role-metadata text-tertiary">
          Rows below this margin are flagged in the "Margin floor" column.
        </p>
      </section>

      <section className="flex flex-col gap-1.5">
        <label htmlFor="pricing-salesperson" className="text-role-section-label">
          Salesperson
        </label>
        <input
          id="pricing-salesperson"
          type="text"
          value={salesperson}
          onChange={(e) => onSalespersonChange(e.target.value)}
          placeholder="Any"
          className="h-7 rounded-xs border border-border-default bg-surface px-2 text-xs text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
        />
      </section>

      <section className="flex flex-col gap-1.5">
        <label htmlFor="pricing-vendor" className="text-role-section-label">
          Vendor
        </label>
        <input
          id="pricing-vendor"
          type="text"
          value={vendorName}
          onChange={(e) => onVendorNameChange(e.target.value)}
          placeholder="Any"
          className="h-7 rounded-xs border border-border-default bg-surface px-2 text-xs text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
        />
      </section>
    </FilterRail>
  )
}
